<template>
  <div class="auth-page">
    <div class="container page">
      <CountyBanner :corpcode="Questionnaire.CorpCode" />

      <section v-if="this.apiRequestError != ''">
        <error-alert :error="this.apiRequestError" />
      </section>

      <section v-if="isFuture" class="alert-info p-3 text-center">
        <strong>IMPORTANT:</strong> This parcel exists in future years only.
      </section>

      <form-wizard
        @on-complete="onComplete"
        @on-change="setTabOrder"
        title
        category
        header
        subtitle
        color="#3f88c7"
        finish-button-text="Return to LTI"
        :hideButtons="applicationFinalized"
        id="wzdMain"
      >
        <tab-content
          title="Disclaimer"
          icon="ti-info"
          :before-change="beforeDisclaimerChange"
        >
          <div class="panel panel-primary">
            <div class="panel-heading PanelHeadingCaption roundedcornerdiv">
              <h4>Property Tax E-Forms</h4>
            </div>
          </div>

          <div class="three-up-body-container">
            <h3 class="section-spacing">Property Information</h3>
            <div class="row rowPadding">
              <div class="col-sm">
                <div class="row">
                  <h6>Parcel Number</h6>
                </div>

                <div class="row">
                  <label class="fw-bold">{{ ParcelNumber }}</label>
                </div>
              </div>
              <div class="col-sm">
                <div class="row">
                  <h6>Address</h6>
                </div>

                <div class="row">
                  <label>{{ PropertyAddress1 }}</label>
                </div>
              </div>
              <div class="col-sm">
                <div class="row">
                  <h6>Type</h6>
                </div>

                <div class="row">
                  <label>{{ PropertyType }}</label>
                </div>
              </div>
            </div>
          </div>

          <div class="three-up-body-container border-0">
            <h3 class="section-spacing mb-1">Notice</h3>
            <p class="section-spacing">
              Applications completed by December 31 will be effective for the
              current year and will reflect on the following years tax bill. For
              example, an application completed by December 31,
              {{ this.DISCLAIMER_PRIOR_TAXYEAR }}, will be reflected on the
              {{ this.DISCLAIMER_CURRENT_TAXYEAR }} tax bill.
            </p>
            <p>
              The property owner may choose one or more of the following forms
              to file electronically:
            </p>

            <div class="container">
              <form class="form-horizontal">
                <div class="row justify-content-md-center">
                  <div class="col-lg-5">
                    <div class="form-group">
                      <div class="checkbox">
                        <label>
                          <input
                            type="checkbox"
                            v-model="Questionnaire.HomesteadSelected"
                          />
                          Homestead Deduction
                        </label>
                      </div>
                    </div>
                    <div v-show="before2023" class="form-group">
                      <div class="checkbox">
                        <label>
                          <input
                            type="checkbox"
                            v-model="Questionnaire.MortgageSelected"
                          />
                          Mortgage Deduction
                        </label>
                      </div>
                    </div>
                    <div
                      v-show="
                        !['DEK', 'POR', 'SJC'].includes(
                          Questionnaire.CorpCode.toUpperCase()
                        )
                      "
                      class="form-group"
                    >
                      <div class="checkbox">
                        <label>
                          <input
                            type="checkbox"
                            v-model="Questionnaire.ChangeOfAddressSelected"
                          />
                          Change of Address Request
                        </label>
                      </div>
                    </div>
                    <div
                      v-show="
                        !['ALN', 'POR', 'SJC'].includes(
                          Questionnaire.CorpCode.toUpperCase()
                        )
                      "
                      class="form-group"
                    >
                      <div class="checkbox">
                        <label>
                          <input
                            type="checkbox"
                            v-model="Questionnaire.RestrictedAddressSelected"
                          />
                          Restricted Property Address Request
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            <div class="form-group">
              <div class="text-center">
                <span
                  v-if="
                    $v.SelectedExemptions.$invalid && this.DisclaimerSubmitted
                  "
                  class="ValidateLabel"
                  >At least one form must be selected to continue.</span
                >
              </div>
            </div>
          </div>
        </tab-content>

        <tab-content
          title="Personal Information"
          icon="ti-user"
          :before-change="beforeCommonQuestionnaireChange"
        >
          <CommonQuestionnaire
            :CommonQuestions="Questionnaire.CommonQuestions"
            :v="$v"
            :CorpCode="Questionnaire.CorpCode"
            ref="commonQ"
          ></CommonQuestionnaire>
        </tab-content>

        <tab-content
          title="E-Tax Form"
          icon="ti-book"
          :before-change="beforeQuestionnaireChange"
        >
          <HomesteadQuestionnaire
            ref="homesteadQ"
            :HomesteadQuestionnaire="Questionnaire.HomesteadQuestionnaire"
            :v="$v"
            v-show="Questionnaire.HomesteadSelected"
            :CorpCode="Questionnaire.CorpCode"
          ></HomesteadQuestionnaire>

          <MortgageQuestionnaire
            ref="mortgageQ"
            :MortgageQuestionnaire="Questionnaire.MortgageQuestionnaire"
            :v="$v"
            v-show="before2023 && Questionnaire.MortgageSelected"
            :CorpCode="Questionnaire.CorpCode"
          ></MortgageQuestionnaire>

          <ChangeOfAddressQuestionnaire
            v-show="Questionnaire.ChangeOfAddressSelected"
            ref="coaQ"
            :CorpCode="Questionnaire.CorpCode"
            :ChangeOfAddressQuestionnaire="
              Questionnaire.ChangeOfAddressQuestionnaire
            "
            :v="$v"
          ></ChangeOfAddressQuestionnaire>

          <RestrictedAddressQuestionnaire
            v-show="Questionnaire.RestrictedAddressSelected"
            ref="rarQ"
            :CorpCode="Questionnaire.CorpCode"
            :RestrictedAddressQuestionnaire="
              Questionnaire.RestrictedAddressQuestionnaire
            "
            :v="$v"
          ></RestrictedAddressQuestionnaire>
        </tab-content>

        <tab-content
          title="Review"
          icon="ti-notepad"
          :before-change="beforeReviewTabSwitch"
        >
          <div class="row Q_Row">
            <embed
              :src="url"
              type="application/pdf"
              width="100%"
              height="700px;"
            />
          </div>

          <div class="row Q_Row">
            <div class="col-sm-4">
              <fieldset class="form-group">
                <label class="Questionniare_Label">Signature</label>
                <input
                  class="form-control form-control-lg"
                  :class="{
                    error:
                      $v.Questionnaire.ReviewProcess.ConfirmationSignature
                        .$invalid && Questionnaire.ReviewTab_Submitted
                  }"
                  type="text"
                  maxlength="100"
                  v-model="
                    $v.Questionnaire.ReviewProcess.ConfirmationSignature.$model
                  "
                  placeholder="John Hancock"
                />
              </fieldset>
            </div>

            <div class="col-sm-4">
              <fieldset class="form-group">
                <label class="Questionniare_Label">Date</label>
                <input
                  disabled
                  class="form-control form-control-lg"
                  :class="{
                    error:
                      $v.Questionnaire.ReviewProcess.ConfirmationSignatureDate
                        .$invalid && Questionnaire.ReviewTab_Submitted
                  }"
                  type="Text"
                  v-mask="'##/##/####'"
                  v-model="
                    $v.Questionnaire.ReviewProcess.ConfirmationSignatureDate
                      .$model
                  "
                  placeholder="mm/dd/yyyy"
                />
              </fieldset>
            </div>

            <div class="col-sm-4">
              <fieldset class="form-group">
                <label class="Questionniare_Label">Confirmation ID:</label>
                <input
                  class="form-control form-control-lg"
                  :class="{
                    error:
                      $v.Questionnaire.ReviewProcess.ConfirmationID.$invalid &&
                      Questionnaire.ReviewTab_Submitted
                  }"
                  type="text"
                  v-mask="'######'"
                  v-model="$v.Questionnaire.ReviewProcess.ConfirmationID.$model"
                  placeholder="123456"
                />
              </fieldset>

              <span class="InformationLabel"
                >Please see email sent to
                {{
                  Questionnaire.CommonQuestions
                    .Applicant_ConfirmationEmailAddress
                }}
                for Confirmation ID required to complete and sign
                application</span
              >

              <span v-if="InvalidConfirmationID" class="ValidateLabel"
                >Invalid Confirmation ID - Please See Confirmation Email for
                Valid ID</span
              >
            </div>
          </div>

          <div
            class="row Q_Row"
            style="align-content:end;flex-direction:column-reverse"
          >
            <label
              :class="{
                error:
                  $v.Questionnaire.ReviewProcess.ConfirmationStatus.$invalid &&
                  Questionnaire.ReviewTab_Submitted
              }"
            >
              <input
                type="checkbox"
                id="Continue"
                name="Continue"
                value="Y"
                :class="{
                  error:
                    $v.Questionnaire.ReviewProcess.ConfirmationStatus
                      .$invalid && Questionnaire.ReviewTab_Submitted
                }"
                v-model="
                  $v.Questionnaire.ReviewProcess.ConfirmationStatus.$model
                "
              />
              I have reviewed my application and would like to submit for
              review.
            </label>
          </div>
        </tab-content>
        <tab-content title="Finished" icon="ti-thumb-up">
          <div class="align-items-center row Q_Row">
            <div class="col-xs-4"></div>
            <div class="col-xs-4 text-center">
              <label class="finalizelabel"
                >Your application has been submitted. A confirmation email has
                been sent to:</label
              >
            </div>
            <div class="col-xs-4"></div>
          </div>

          <div class="align-items-center row Q_Row">
            <div class="col-xs-4"></div>
            <div class="col-xs-4 text-center">
              <label class="finalizelabel"
                >{{
                  Questionnaire.CommonQuestions.Applicant_EmailAddress
                }}.</label
              >
            </div>
            <div class="col-xs-4"></div>
          </div>
        </tab-content>
        <button
          ref="backBtn"
          slot-scope="props"
          slot="prev"
          class="wizard-btn"
          :style="props.fillButtonStyle"
        >
          Back
        </button>
        <button
          ref="nextBtn"
          v-if="!props.isLastStep"
          :disabled="
            props.activeTabIndex == 3 &&
              Questionnaire.ReviewProcess.ConfirmationStatus == ''
          "
          slot-scope="props"
          slot="next"
          class="wizard-btn"
          :style="props.fillButtonStyle"
        >
          {{ getNextButtonText(props.activeTabIndex) }}
        </button>
      </form-wizard>
      <!-- </form>-->
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";

import axios from "axios";
import Vue from "vue";
import CommonQuestionnaire from "@/components/CommonQuestionnaire";
import HomesteadQuestionnaire from "@/components/HomesteadQuestionnaire";
import MortgageQuestionnaire from "@/components/MortgageQuestionnaire";
import ChangeOfAddressQuestionnaire from "@/components/ChangeOfAddressQuestionnaire";
import RestrictedAddressQuestionnaire from "@/components/RestrictedAddressQuestionnaire";
import CountyBanner from "../components/county/Banner";

import { required } from "vuelidate/lib/validators";

import ErrorAlert from "../components/ErrorAlert.vue";

//custom validator for the exemption selection requiring at least one
export function ValidateSelections(Value) {
  return (
    this.Questionnaire.HomesteadSelected ||
    (this.before2023 && this.Questionnaire.MortgageSelected) ||
    this.Questionnaire.ChangeOfAddressSelected ||
    this.Questionnaire.RestrictedAddressSelected
  );
}

export function ValidateConfirmationCheckbox(Value) {
  var isInvalid = Value == false;

  return !isInvalid;
}

export default {
  name: "QuestionnaireMain",
  data() {
    return {
      //application specific vars
      BaseURL: process.env.VUE_APP_API_URL,
      CorpCode: "",
      payyear: parseInt(this.$route.query.payyear) || null,
      isFuture: false,
      currentDate: new Date(), // default to local date, overwritten in mounted()
      currentYear: new Date().getFullYear(),
      nextButtonText: "Next",

      //from the first selection page
      SelectedExemptions: false,
      DisplayLoginInfo: false,
      PropertyAddress1: "",
      PropertyAddress2: "",
      PropertyCity: "",
      PropertyState: "",
      PropertyZipCode: "",
      PropertyZipPlus4: "",
      PropertyType: "",
      ParcelNumber: "",
      SubmitStatus: "",
      applicationFinalized: false,
      reviewURL: "http://192.168.25.16/LowTaxExemptionPDF/test.pdf",
      url: "",
      submitted: false,
      DisclaimerSubmitted: false,
      InvalidConfirmationID: false,
      SubmitDate: "",
      OnReviewPage: false,
      apiRequestError: "",
      DISCLAIMER_PRIOR_TAXYEAR: "",
      DISCLAIMER_CURRENT_TAXYEAR: "",

      Questionnaire: {
        MortgageSelected: false,
        HomesteadSelected: process.env.NODE_ENV === "development",
        ChangeOfAddressSelected: process.env.NODE_ENV === "development",
        RestrictedAddressSelected: process.env.NODE_ENV === "development",
        //store duplicate number passed in on mount
        DuplicateNumber: "",
        CorpCode: "",
        Application_SessionID: "",
        SubmitFinalize: false,
        ReviewTab_Submitted: false,
        //separate our data structure into categories of questions
        CommonQuestions: {
          Applicant_FirstName:
            process.env.NODE_ENV === "development" ? "TEST" : "",
          Applicant_LastName:
            process.env.NODE_ENV === "development" ? "TEST" : "",
          Applicant_TelephoneNumber:
            process.env.NODE_ENV === "development" ? "5555555555" : "",
          Applicant_Address1:
            process.env.NODE_ENV === "development" ? "TEST" : "",
          Applicant_Address2: "",
          Applicant_City:
            process.env.NODE_ENV === "development" ? "ANYWHERE" : "",
          Applicant_State: process.env.NODE_ENV === "development" ? "IN" : "",
          Applicant_ZipCode:
            process.env.NODE_ENV === "development" ? "46512" : "",
          Applicant_EmailAddress:
            process.env.NODE_ENV === "development"
              ? "patrickrenneker@lllow.com"
              : "",
          Applicant_ConfirmationEmailAddress:
            process.env.NODE_ENV === "development"
              ? "patrickrenneker@lllow.com"
              : "",
          //using this to make sure the edits don't display prior to clicking next
          Common_Questionnaire_Submitted: false
        },
        HomesteadQuestionnaire: {
          OwnershipStatus: process.env.NODE_ENV === "development" ? "O" : "",
          ContractSeller: "",
          ClaimantSSNLastFive:
            process.env.NODE_ENV === "development" ? "12345" : "",
          ClaimantDLNumber:
            process.env.NODE_ENV === "development" ? "12345" : "",
          ClaimantDLState: process.env.NODE_ENV === "development" ? "IN" : "",
          ClaimaintVacatingHomestead:
            process.env.NODE_ENV === "development" ? 0 : -1,
          ClaimantPropertyOwnedElsewhere:
            process.env.NODE_ENV === "development" ? 0 : -1,
          ClaimantMarried: process.env.NODE_ENV === "development" ? 0 : -1,
          SpouseClaimantLastName: "",
          SpouseClaimantFirstName: "",
          SpouseTelephoneNumber: "",
          SpouseSSNLastFive: "",
          SpouseDLNumber: "",
          SpouseDLState: "",
          OtherPropertyStateCountyTownship: "",
          VacatedHomesteadAddress: "",
          //don't display edits unless the Next was clicked
          Exemption_Questionnaire_Submitted: false,
          SpouseOrAdditionalDeededOwner_NotApplicable:
            process.env.NODE_ENV === "development"
        },

        //review process fields - confirmation/signature
        ReviewProcess: {
          ConfirmationSignature: "",
          ConfirmationSignatureDate: "",
          ConfirmationStatus: "",
          ConfirmationID: ""
        },

        MortgageQuestionnaire: {
          Exemption_Questionnaire_Submitted: false,
          DateOfMortgage:
            process.env.NODE_ENV === "development" ? "01/02/2022" : "",
          PhoneNumber: "",
          EmailAddress: "",
          OriginalMortgageAmount:
            process.env.NODE_ENV === "development" ? "100000" : "",
          OriginalLenderOrContractSeller:
            process.env.NODE_ENV === "development" ? "TEST" : "",
          LenderAddress1: process.env.NODE_ENV === "development" ? "TEST" : "",
          LenderAddress2: process.env.NODE_ENV === "development" ? "TEST" : "",
          LenderCity:
            process.env.NODE_ENV === "development" ? "Beverly Hills" : "",
          LenderState: process.env.NODE_ENV === "development" ? "CA" : "",
          LenderZipCode: process.env.NODE_ENV === "development" ? "90210" : ""
        },
        ChangeOfAddressQuestionnaire: {
          MailingAddress1: "",
          MailingAddress2: "",
          MailingCity: "",
          MailingState: "",
          MailingZipCode: "",
          MailingZipPlus4: "",
          MailingCountry: "",
          NewMailingAddress1:
            process.env.NODE_ENV === "development" ? "TEST" : "",
          NewMailingAddress2:
            process.env.NODE_ENV === "development" ? "TEST" : "",
          NewMailingCity:
            process.env.NODE_ENV === "development" ? "Beverly Hills" : "",
          NewMailingState: process.env.NODE_ENV === "development" ? "CA" : "",
          NewMailingZipCode:
            process.env.NODE_ENV === "development" ? "90210" : "",
          NewMailingZipPlus4:
            process.env.NODE_ENV === "development" ? "90210" : "",
          NewMailingCountry: "",
          Submitted: false
        },
        RestrictedAddressQuestionnaire: {
          CoveredPersonIs: "A judge.",
          CoveredPersonName:
            process.env.NODE_ENV === "development" ? "TEST" : "",
          JobTitleDepartmentOffice:
            process.env.NODE_ENV === "development" ? "TEST" : "",
          Submitted: false
        }
      }
    };
  },

  validations: {
    //require at least one exemption to be selected
    SelectedExemptions: {
      ValidateSelections
    },

    //on the review page we require 3 things to proceed with
    //finalizing the app - Signature, signature date, and checkbox confirmation

    Questionnaire: {
      ReviewProcess: {
        ConfirmationSignature: {
          required
        },
        ConfirmationSignatureDate: {
          required
        },
        ConfirmationStatus: {
          ValidateConfirmationCheckbox
        },
        ConfirmationID: {
          required
        }
      }
    }
  },
  components: {
    ErrorAlert,
    CommonQuestionnaire,
    HomesteadQuestionnaire,
    MortgageQuestionnaire,
    ChangeOfAddressQuestionnaire,
    RestrictedAddressQuestionnaire,
    CountyBanner
  },
  computed: {
    ...mapState({
      errors: state => state.auth.errors
    }),
    before2023() {
      return this.currentYear < 2023;
    }
  },
  mounted() {
    //TODO: need to add exception handling here to error out if there's no dupe provided
    var dup = this.$route.query.dup;
    var corpCode = this.$route.query.CorpCode;
    this.Questionnaire.DuplicateNumber = dup;
    this.Questionnaire.CorpCode = corpCode;
    this.CorpCode = corpCode;
    this.getPropertyInfo(dup);
    this.isFuture =
      this.apiRequestError == "" &&
      this.payyear &&
      this.payyear > this.currentYear;
    this.DISCLAIMER_PRIOR_TAXYEAR = this.currentYear;
    this.DISCLAIMER_CURRENT_TAXYEAR = this.currentYear + 1;
  },
  methods: {
    getNextButtonText(stepIndex) {
      return stepIndex === 3 ? "Submit" : "Next";
    },
    setTabOrder(prevIndex, nextIndex) {
      if (prevIndex == 0) {
        // set tabindex of Next button to be prior to that of Back button
        this.$nextTick(() => {
          let elements = document.querySelectorAll("input,select");
          for (let i = 0; i < elements.length; i++) {
            let el = elements[i];
            el.setAttribute("tabindex", 1);
          }
          if (this.$refs.backBtn) {
            this.$refs.nextBtn.parentElement.setAttribute("tabindex", 2);
            this.$refs.nextBtn.setAttribute("tabindex", 2);
            this.$refs.backBtn.parentElement.setAttribute("tabindex", 3);
            this.$refs.backBtn.setAttribute("tabindex", 3);
          }
        });
      }
    },
    //return property information by duplicate number
    getPropertyInfo(duplicatenumber) {
      this.apiRequestError = "";
      const payYearStr = this.payyear ? "&PayYear=" + this.payyear : "";
      axios
        .get(
          this.BaseURL +
            "PropertyInfo?duplicateNumber=" +
            duplicatenumber +
            "&CorpCode=" +
            this.CorpCode +
            payYearStr,
          { timeout: 5000 }
        )
        .then(response => {
          var currentRow = response.data[0];
          //setup vue varibles with data from property lookup
          this.currentDate = new Date(currentRow.CurrentDate);
          this.currentYear = this.currentDate.getFullYear();
          this.PropertyAddress1 = currentRow.PropertyAddress1;
          this.PropertyAddress2 = currentRow.PropertyAddress2;
          this.PropertyCity = currentRow.PropertyCity;
          this.PropertyState = currentRow.PropertyState;
          this.PropertyZipCode = currentRow.PropertyZipCode;
          this.PropertyZipPlus4 = currentRow.PropertyZipPlus4;
          this.PropertyType = currentRow.TaxType;
          this.ParcelNumber = currentRow.StateKey;
          this.Questionnaire.HomesteadQuestionnaire.ContractSeller =
            currentRow.FutureOwnerOfRecord;
          this.Questionnaire.ReviewProcess.ConfirmationSignatureDate =
            (this.currentDate.getMonth() + 1 > 9
              ? this.currentDate.getMonth() + 1
              : "0" + (this.currentDate.getMonth() + 1)) +
            "/" +
            (this.currentDate.getDate() > 9
              ? this.currentDate.getDate()
              : "0" + this.currentDate.getDate()) +
            "/" +
            this.currentYear;
          this.Questionnaire.ChangeOfAddressQuestionnaire.MailingAddress1 =
            currentRow.MailingAddress1;
          this.Questionnaire.ChangeOfAddressQuestionnaire.MailingAddress2 =
            currentRow.MailingAddress2;
          this.Questionnaire.ChangeOfAddressQuestionnaire.MailingCity =
            currentRow.MailingCity;
          this.Questionnaire.ChangeOfAddressQuestionnaire.MailingState =
            currentRow.MailingState;
          this.Questionnaire.ChangeOfAddressQuestionnaire.MailingZipCode =
            currentRow.MailingZipCode;
          this.Questionnaire.ChangeOfAddressQuestionnaire.MailingZipPlus4 =
            currentRow.MailingZipPlus4;
          this.Questionnaire.ChangeOfAddressQuestionnaire.MailingCountry =
            currentRow.MailingCountry;
        })
        .catch(e => {
          this.apiRequestError =
            "An error has occurred and the application process cannot continue. Please try again later.";
        });
    },
    onComplete() {},
    beforeDisclaimerChange: function() {
      this.$v.SelectedExemptions.$touch();
      this.DisclaimerSubmitted = true;

      var shouldChangePage = !this.$v.SelectedExemptions.$invalid;

      if (shouldChangePage) {
        window.scrollTo(0, 0);
      }
      return shouldChangePage;
    },
    beforeCommonQuestionnaireChange: function() {
      //validate the common questions
      this.$refs.commonQ.$v.$touch();
      this.Questionnaire.CommonQuestions.Common_Questionnaire_Submitted = true;

      var shouldChangePage = !this.$refs.commonQ.$v.$invalid;

      if (shouldChangePage) {
        //default mortgage questionnaire phone/email to the values entered on the applicant form
        this.Questionnaire.MortgageQuestionnaire.PhoneNumber = this.Questionnaire.CommonQuestions.Applicant_TelephoneNumber;
        this.Questionnaire.MortgageQuestionnaire.EmailAddress = this.Questionnaire.CommonQuestions.Applicant_EmailAddress;

        //scroll to top of the page
        window.scrollTo(0, 0);
      } else {
        this.$nextTick(() => {
          let el = document.querySelector("input.error");
          if (el) {
            el.getBoundingClientRect();
            window.scrollTo(
              el.left + document.documentElement.scrollLeft,
              el.top + document.documentElement.scrollTop - 40
            );
          }
        });
      }
      return shouldChangePage;
    },
    beforeQuestionnaireChange: function() {
      //validate homestead questionnaire entry
      if (this.Questionnaire.HomesteadSelected) {
        this.$refs.homesteadQ.$v.$touch();
        this.Questionnaire.HomesteadQuestionnaire.Exemption_Questionnaire_Submitted = true;
      }
      //validate mortgage questionniare
      if (this.before2023 && this.Questionnaire.MortgageSelected) {
        this.$refs.mortgageQ.$v.$touch();
        this.Questionnaire.MortgageQuestionnaire.Exemption_Questionnaire_Submitted = true;
      }
      //validate change of address questionniare
      if (this.Questionnaire.ChangeOfAddressSelected) {
        this.$refs.coaQ.$v.$touch();
        this.Questionnaire.ChangeOfAddressQuestionnaire.Submitted = true;
      }
      //validate restricted address questionniare
      if (this.Questionnaire.RestrictedAddressSelected) {
        this.$refs.rarQ.$v.$touch();
        this.Questionnaire.RestrictedAddressQuestionnaire.Submitted = true;
      }

      //if there are any validation issues then don't let the page change
      if (
        (this.Questionnaire.HomesteadSelected &&
          this.$refs.homesteadQ.$v.$invalid) ||
        (this.Questionnaire.MortgageSelected &&
          this.$refs.mortgageQ.$v.$invalid) ||
        (this.Questionnaire.ChangeOfAddressSelected &&
          !this.isFuture &&
          this.$refs.coaQ.$v.$invalid) ||
        (this.Questionnaire.RestrictedAddressSelected &&
          this.$refs.rarQ.$v.$invalid)
      ) {
        this.$nextTick(() => {
          let el = document.querySelector("input.error");
          if (el) {
            el.getBoundingClientRect();
            window.scrollTo(
              el.left + document.documentElement.scrollLeft,
              el.top + document.documentElement.scrollTop - 40
            );
          }
        });
        return false;
      } else {
        var jsonQuestionnaireData = JSON.stringify(this.Questionnaire);
        this.apiRequestError = "";
        axios
          .post(
            this.BaseURL + "SubmitApplication",
            jsonQuestionnaireData,
            {
              headers: {
                "Content-Type": "application/json"
              },
              params: {
                CorpCode: this.CorpCode,
                PayYear: this.isFuture ? this.payyear : null
              }
            },
            { timeout: 5000 }
          )
          //response has the file name of the PDF
          .then(response => {
            var responseData = response.data;
            this.OnReviewPage = true;
            this.Questionnaire.Application_SessionID =
              responseData.Application_SessionID;
            this.url = responseData.PDF_URL;
          })
          .catch(e => {
            this.apiRequestError =
              "An error has occurred and your application cannot be submitted.";
          });
        return true;
      }
    },

    beforeReviewTabSwitch: function() {
      return new Promise((resolve, reject) => {
        //validate the review process fields
        this.Questionnaire.ReviewTab_Submitted = true;
        this.$v.Questionnaire.ReviewProcess.$touch();

        if (this.$v.Questionnaire.ReviewProcess.$invalid) {
          this.$nextTick(() => {
            let el = document.querySelector("input.error");
            if (el) {
              el.getBoundingClientRect();
              window.scrollTo(
                el.left + document.documentElement.scrollLeft,
                el.top + document.documentElement.scrollTop - 40
              );
            }
          });
          resolve(false);
        } else {
          var jsonQuestionnaireData = JSON.stringify(this.Questionnaire);
          axios
            .post(this.BaseURL + "FinalizeApplication", jsonQuestionnaireData, {
              headers: {
                "Content-Type": "application/json"
              },
              params: {
                CorpCode: this.CorpCode
              }
            })
            .then(response => {
              var responseData = response.data;
              if (responseData.Error == "") {
                this.applicationFinalized = true;
                this.InvalidConfirmationID = false;
                resolve(true);
              } else {
                this.InvalidConfirmationID = true;
                resolve(false);
              }
            });
        }
      });
    }
  }
};
</script>
